import { faroCoreApiClient } from "@api/faro-core-api-client";
import { createAsyncThunk } from "@reduxjs/toolkit";
import { UtilsTypes } from "@stellar/utils";
import { sortGroupMembersByRemovalOrder } from "@utils/group-utils";
import { projectStateToApiArchivingState } from "@utils/project-utils";

/** Updates a member role in a group using the backend, see updateMemberRoleInGroup */
async function updateMemberRoleInGroupBase({
  companyId,
  groupId,
  role,
  userIdentity
}) {
  await faroCoreApiClient.groups.updateGroupMemberRole({
    companyId,
    groupId,
    userIdentity,
    payload: {
      role
    }
  });
  return {
    groupId,
    role,
    userIdentity
  };
}

/** Removes a member from a group using the backend, see removeMemberFromGroup */
async function removeMemberFromGroupBase({
  companyId,
  groupId,
  member,
  onGroupRemoved
}) {
  await faroCoreApiClient.groups.removeMemberFromGroup({
    companyId,
    groupId,
    userIdentity: member.identity
  });
  return {
    groupId,
    companyId,
    member,
    onGroupRemoved
  };
}

/**
 * Update a member role in the group using the backend,
 * and then update it in group in the store as well
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const updateMemberRoleInGroup = createAsyncThunk("groups/updateMemberRoleInGroup", updateMemberRoleInGroupBase);

/**
 * Same as updateMemberRoleInGroup, for callers that tell the user about a failure themselves:
 * unlike updateMemberRoleInGroup, its errors are not shown as a toast by the errors slice.
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const updateMemberRoleInGroupWithoutErrorToast = createAsyncThunk("groups/updateMemberRoleInGroupWithoutErrorToast", updateMemberRoleInGroupBase);

/**
 * Fetches groups from the backend so they can be put into the store
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchGroups = createAsyncThunk("groups/fetchGroups", async ({
  companyId
}) => {
  return await faroCoreApiClient.groups.getGroups({
    companyId
  });
});

/** Fetches the details of a group */
/**
 * Fetches the details of a group
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchGroupDetails = createAsyncThunk("groups/fetchGroupDetails", async ({
  companyId,
  groupId
}) => {
  return await faroCoreApiClient.groups.getGroupById({
    companyId,
    groupId
  });
});

/**
 * Creates a group in the backend and adds it to the store
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const addGroup = createAsyncThunk("groups/addGroup", async ({
  companyId,
  groupName,
  selectedGroupManagerIds
}) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const {
    message,
    ...group
  } = await faroCoreApiClient.groups.addGroup({
    companyId,
    payload: {
      name: groupName,
      assignments: [{
        identities: selectedGroupManagerIds,
        role: "COMPANY_MANAGER"
      }]
    }
  });
  return group;
});

/**
 * Update group details
 * @throws {Error} if no companyId or groupId exist to update the group details
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const updateGroupDetails = createAsyncThunk("groups/updateGroupDetails", async ({
  groupId,
  payload
}, {
  getState
}) => {
  const {
    groups: {
      selectedGroupId
    },
    sdbCompany: {
      selectedSdbCompanyId
    }
  } = getState();

  /**
   * The ID of the group that is going to be updated.
   * If groupId is not provided, the selectedGroupId from store is selected
   */
  const updatingGroupId = groupId || selectedGroupId;
  if (!selectedSdbCompanyId || !updatingGroupId) {
    throw new Error("No companyId or groupId exist to was given to updateGroupDetails");
  }
  return await faroCoreApiClient.groups.updateGroupDetails({
    companyId: selectedSdbCompanyId,
    groupId: updatingGroupId,
    payload
  });
});

/**
 * Remove a member from the group using the backend,
 * and then removes it from group in the store as well
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const removeMemberFromGroup = createAsyncThunk("groups/removeMemberFromGroup", removeMemberFromGroupBase);

/**
 * Same as removeMemberFromGroup, for callers that tell the user about a failure themselves:
 * unlike removeMemberFromGroup, its errors are not shown as a toast by the errors slice.
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const removeMemberFromGroupWithoutErrorToast = createAsyncThunk("groups/removeMemberFromGroupWithoutErrorToast", removeMemberFromGroupBase);

/**
 * Deletes a group by unassigning every one of its members: there is no endpoint to delete a
 * group, the backend drops it as soon as its last member is unassigned. The members are
 * unassigned one at a time and in the order the backend accepts, see
 * sortGroupMembersByRemovalOrder.
 * @throws {HttpUtils.FetchError} if any of the Core API client requests fails
 */
export const removeGroup = createAsyncThunk("groups/removeGroup", async ({
  companyId,
  groupId,
  members
}, {
  getState
}) => {
  const {
    user: {
      currentUser
    }
  } = getState();
  for (const member of sortGroupMembersByRemovalOrder(members, currentUser?.identity)) {
    await faroCoreApiClient.groups.removeMemberFromGroup({
      companyId,
      groupId,
      userIdentity: member.identity
    });
  }
});

/**
 * Fetches how many projects a group holds. The group's own numProjects attribute counts only the
 * active ones, so the archived projects have to be asked for explicitly. The endpoint takes no
 * page size, so the total comes together with a first page of projects.
 * @throws {HttpUtils.FetchError} if the Core API client request fails
 */
export const fetchGroupProjectsCount = createAsyncThunk("groups/fetchGroupProjectsCount", async ({
  companyId,
  groupId
}) => {
  const {
    page
  } = await faroCoreApiClient.projects.getProjects({
    companyId,
    groupId,
    archivingStates: [...projectStateToApiArchivingState(UtilsTypes.ProjectArchivingState.active), ...projectStateToApiArchivingState(UtilsTypes.ProjectArchivingState.archived)]
  });
  return {
    groupId,
    projectsCount: page.total
  };
});